/**
 * 영업진행 연동 모달 — 견적서를 영업건에 신규 등록 또는 기존 갱신.
 *
 * 흐름:
 *   1) 모드 선택: [신규 영업건 등록] / [기존 영업건 갱신]
 *   2) 신규: 미리보기(매칭) + 확인 클릭 → POST /quotes/:id/link-sales {mode:'new'}
 *   3) 갱신: 영업건 검색(q + companyId) → 결과 클릭 → 확인 → {mode:'update', targetSalesId}
 *
 * 성공 시 결과 표시 (매출일련번호 + PDF 자동 첨부 여부).
 */

import { useEffect, useMemo, useState } from 'react';
import { listSales } from '../../lib/sales-api';
import type { SalesListRow } from '../../lib/types';
import { linkQuoteToSales, type QuoteDetail, type QuoteLinkSalesResult } from '../../lib/quote-api';

interface Props {
  open: boolean;
  quote: QuoteDetail | null;
  onClose: () => void;
}

type Step = 'choose' | 'new-confirm' | 'update-search' | 'update-confirm' | 'result';

/** 연동 화면에서 편집 가능한 매핑 값 (모두 문자열로 보관 — 제안가는 입력 편의상 문자열) */
interface MappingForm {
  proposalCompany: string;
  contactPerson: string;
  endUser: string;
  projectName: string;
  itemSummary: string;
  proposalPrice: string;
}

const EMPTY_FORM: MappingForm = {
  proposalCompany: '',
  contactPerson: '',
  endUser: '',
  projectName: '',
  itemSummary: '',
  proposalPrice: '',
};

export function LinkSalesModal({ open, quote, onClose }: Props) {
  const [step, setStep] = useState<Step>('choose');
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<SalesListRow[]>([]);
  const [selected, setSelected] = useState<SalesListRow | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<QuoteLinkSalesResult | null>(null);
  const [form, setForm] = useState<MappingForm>(EMPTY_FORM);

  /**
   * 열 때마다 초기화.
   * ⚠ deps 에 **견적서 id 도 넣는다** — 부모가 모달을 닫지 않고 quote 만 바꿔 다시 열면
   *   `open` 은 계속 true 라 초기화가 안 돌고, 앞 견적서에서 만진 상태가 그대로 남는다.
   *   (실제로 겪었다: 앞 견적서에서 「수신 업체만 보기」를 끈 채 다음 견적서를 열었더니
   *    꺼진 상태로 열려 엉뚱한 업체 영업건이 목록에 그대로 있었다.)
   */
  useEffect(() => {
    if (!open) return;
    setStep('choose');
    setQuery('');
    setSearchResults([]);
    setSelected(null);
    setSubmitting(false);
    setErr(null);
    setResult(null);
    setForm(EMPTY_FORM);
    setOnlyRecipient(true); // 수신 업체로 좁혀 보기 — 기본 켜짐
  }, [open, quote?.id]);

  // 매칭 미리보기 (견적서 → 영업건 매핑)
  const preview = useMemo(() => {
    if (!quote) return null;
    const firstDescLine = (quote.items[0]?.description || quote.items[0]?.itemName || '').split('\n')[0]?.trim() ?? '';
    const itemSummary = quote.items.length > 1 ? `${firstDescLine} 외` : firstDescLine;
    return {
      companyId: quote.companyId,
      empDisplay: quote.empDisplay,
      proposalCompany: quote.recipientCompany,
      contactPerson: quote.reference,
      projectName: quote.projectName,
      itemSummary,
      proposalPrice: quote.totalSupply,
    };
  }, [quote]);

  /**
   * 수신 업체로 좁혀 보기 (v2.79.9 사용자 08-21) — 기본 켜짐.
   *
   * 견적서를 기존 영업건에 붙일 때, 그 영업건은 **그 견적서를 받는 업체의 것**일 수밖에
   * 없다. 그래서 처음부터 그 업체 것만 보여준다(운영 실측: 후보 30건 → 0~13건).
   * ⚠ 다만 **0건이 되는 견적서가 실제로 있다**(운영 Q-2026-SM-0079 영등포구청).
   *   그때 목록만 비어 있으면 「왜 안 나오지?」가 되므로, 끄고 전체에서 찾을 수 있게 둔다.
   */
  const [onlyRecipient, setOnlyRecipient] = useState(true);
  const recipient = quote?.recipientCompany?.trim() ?? '';

  async function runSearch(opts?: { onlyRecipient?: boolean }) {
    if (!quote) return;
    const narrow = opts?.onlyRecipient ?? onlyRecipient;
    setSearching(true);
    setErr(null);
    try {
      const r = await listSales({
        q: query.trim() || undefined,
        companyId: quote.companyId,
        // 수신 업체 정확 일치 — 서버가 제안업체(proposal_company) 칸으로 거른다
        proposalCompany: narrow && recipient !== '' ? recipient : undefined,
        // 발행/판보 상태는 견적서 연동 불가 — 검색 결과에서 제외
        stage: ['유력', '발주', '납품', '일반'],
        pageSize: 30,
      });
      setSearchResults(r.data);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSearching(false);
    }
  }

  // 첫 진입 시 자동으로 같은 사업자 영업건 30건 미리 로드
  useEffect(() => {
    if (open && step === 'update-search' && searchResults.length === 0 && !searching) {
      runSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, step]);

  // 자동완성 표 → 편집 폼 초기화. 신규는 견적 매핑값, 갱신은 매핑값 + 기존 엔드유저 프리필.
  function initFormForNew() {
    if (!preview) return;
    setForm({
      proposalCompany: preview.proposalCompany || '',
      contactPerson: preview.contactPerson || '',
      endUser: '',
      projectName: preview.projectName || '',
      itemSummary: preview.itemSummary || '',
      proposalPrice: String(preview.proposalPrice ?? 0),
    });
  }
  function initFormForUpdate(row: SalesListRow) {
    if (!preview) return;
    setForm({
      proposalCompany: preview.proposalCompany || '',
      contactPerson: preview.contactPerson || '',
      endUser: row.endUser || '',
      projectName: preview.projectName || '',
      itemSummary: preview.itemSummary || '',
      proposalPrice: String(preview.proposalPrice ?? 0),
    });
  }

  async function submitLink(mode: 'new' | 'update') {
    if (!quote) return;
    setSubmitting(true);
    setErr(null);
    try {
      // 편집한 값(빈칸이면 null). 제안가는 콤마 제거 후 숫자.
      const overrides = {
        proposalCompany: form.proposalCompany.trim() || null,
        contactPerson: form.contactPerson.trim() || null,
        projectName: form.projectName.trim() || null,
        itemSummary: form.itemSummary.trim() || null,
        endUser: form.endUser.trim() || null,
        proposalPrice: Number(form.proposalPrice.replace(/[,\s]/g, '')) || 0,
      };
      const body: Parameters<typeof linkQuoteToSales>[1] =
        mode === 'new'
          ? { mode: 'new', overrides }
          : { mode: 'update', targetSalesId: selected!.salesId, overrides };
      const r = await linkQuoteToSales(quote.id, body);
      setResult(r);
      setStep('result');
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  if (!open || !quote) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-start justify-center overflow-y-auto p-4">
      <div
        className="bg-white rounded shadow-xl w-full max-w-2xl my-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h2 className="ui-modal-title">영업진행 연동</h2>
          <button
            type="button"
            onClick={onClose}
            className="ui-close"
          >
            ×
          </button>
        </div>

        <div className="p-5">
          {err && (
            <div className="mb-3 px-3 py-2 bg-rose-50 border border-rose-200 text-rose-800 rounded text-sm whitespace-pre-wrap">
              {err}
            </div>
          )}

          {step === 'choose' && (
            <div>
              <p className="text-sm text-slate-600 mb-4">
                이 견적서를 영업진행 화면의 영업건에 연동합니다.<br />
                견적서 PDF 가 자동으로 해당 영업건에 첨부됩니다 (마지막 견적서만 유지).
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => {
                    initFormForNew();
                    setStep('new-confirm');
                  }}
                  className="px-4 py-6 text-center border-2 border-blue-200 hover:border-blue-500 hover:bg-blue-50 rounded transition"
                >
                  <div className="text-2xl mb-1">➕</div>
                  <div className="font-bold text-slate-800">신규 영업건 등록</div>
                  <div className="text-xs text-slate-500 mt-1">새 매출일련번호 채번</div>
                </button>
                <button
                  type="button"
                  onClick={() => setStep('update-search')}
                  className="px-4 py-6 text-center border-2 border-amber-200 hover:border-amber-500 hover:bg-amber-50 rounded transition"
                >
                  <div className="text-2xl mb-1">🔄</div>
                  <div className="font-bold text-slate-800">기존 영업건 정보 갱신</div>
                  <div className="text-xs text-slate-500 mt-1">사업자별 검색 후 선택</div>
                </button>
              </div>
            </div>
          )}

          {step === 'new-confirm' && preview && (
            <div>
              <h3 className="ui-subsection-title mb-2">신규 영업건 등록 — 내용 확인·수정</h3>
              <p className="mb-2 text-xs text-slate-500">
                견적서에서 자동으로 채워진 값입니다. 필요하면 아래에서 직접 고칠 수 있어요. 엔드유저는 비워둬도 등록됩니다.
              </p>
              <MappingEditor readonly={{ companyId: preview.companyId, empDisplay: preview.empDisplay }} form={form} setForm={setForm} />
              <div className="mt-3 text-xs text-slate-500">
                · status 는 <b>'유력'</b> 으로 등록 (견적서 연동건)<br />
                · note 에 "견적서 {quote.quoteNo} 에서 자동 등록" 자동 기록<br />
                · 견적서 PDF 도 영업건 첨부파일에 자동 추가됩니다
              </div>
            </div>
          )}

          {step === 'update-search' && (
            <div>
              <h3 className="ui-subsection-title mb-2">
                {quote.companyId} 사업자 영업건 검색
              </h3>
              <div className="mb-2 text-xs text-slate-500">
                발행/판보 상태의 영업건은 갱신 불가 — 목록에 표시되지 않습니다
              </div>
              {/*
                수신 업체로 좁혀 보기 (v2.79.9) — 기본 켜짐. 무엇으로 좁혔는지 밝히고,
                끌 수 있게 둔다(수신 업체 영업건이 0건인 견적서가 실제로 있다).
              */}
              {recipient !== '' && (
                <div className="mb-2 flex items-center gap-2 text-xs bg-blue-50 border border-blue-200 rounded px-2 py-1.5">
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={onlyRecipient}
                      onChange={(e) => {
                        const next = e.target.checked;
                        setOnlyRecipient(next);
                        void runSearch({ onlyRecipient: next });
                      }}
                    />
                    <span className="text-slate-700">
                      <b>{recipient}</b> 의 영업건만 보기
                    </span>
                  </label>
                  {!onlyRecipient && (
                    <span className="text-slate-400">— 지금은 전체에서 찾는 중</span>
                  )}
                </div>
              )}
              <div className="flex gap-2 mb-2">
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') runSearch();
                  }}
                  placeholder="매출일련번호 / 프로젝트명 / 제안업체 / 엔드유저 / 계산서발행"
                  className="ui-input flex-1"
                />
                <button
                  type="button"
                  // ⚠ onClick={runSearch} 로 두면 MouseEvent 가 opts 로 들어간다 — 감싸서 넘긴다
                  onClick={() => void runSearch()}
                  disabled={searching}
                  className="btn btn-primary"
                >
                  {searching ? '검색중...' : '검색'}
                </button>
              </div>
              <div className="max-h-80 overflow-y-auto border border-slate-200 rounded">
                {searchResults.length === 0 && !searching ? (
                  /* 빈 목록은 이유를 말해야 한다 — 좁혀서 빈 것인지, 정말 없는 것인지 */
                  onlyRecipient && recipient !== '' ? (
                    <div className="text-center text-slate-500 py-6 text-sm px-4">
                      <div>
                        {/* 검색어까지 걸려 있으면 그것도 이유일 수 있다 — 정확히 짚어 준다 */}
                        {query.trim() !== '' ? (
                          <>
                            <b>{recipient}</b> 의 영업건 중 「{query.trim()}」 에 맞는 건이 없습니다.
                          </>
                        ) : (
                          <>
                            <b>{recipient}</b> 로 등록된 갱신 가능한 영업건이 없습니다.
                          </>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setOnlyRecipient(false);
                          void runSearch({ onlyRecipient: false });
                        }}
                        className="btn btn-secondary btn-sm mt-2"
                      >
                        전체에서 찾기
                      </button>
                    </div>
                  ) : (
                    <div className="text-center text-slate-400 py-6 text-sm">검색 결과 없음</div>
                  )
                ) : (
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="px-2 py-1 text-left">매출번호</th>
                        <th className="px-2 py-1 text-left">제안업체</th>
                        <th className="px-2 py-1 text-left">프로젝트명</th>
                        <th className="px-2 py-1 text-left">진행</th>
                      </tr>
                    </thead>
                    <tbody>
                      {searchResults.map((r) => (
                        <tr
                          key={r.salesId}
                          onClick={() => {
                            setSelected(r);
                            initFormForUpdate(r);
                            setStep('update-confirm');
                          }}
                          className="border-t border-slate-100 hover:bg-amber-50 cursor-pointer"
                        >
                          <td className="px-2 py-1 font-mono">{r.salesId}</td>
                          <td className="px-2 py-1">{r.proposalCompany ?? '-'}</td>
                          <td className="px-2 py-1">{r.projectName ?? '-'}</td>
                          <td className="px-2 py-1">{r.status}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}

          {step === 'update-confirm' && preview && selected && (
            <div>
              <h3 className="ui-subsection-title mb-2">
                영업건 갱신 — <span className="font-mono">{selected.salesId}</span>
              </h3>
              <div className="mb-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900">
                <div className="font-medium mb-0.5">기존 영업건 (변경 전)</div>
                <div>제안업체: {selected.proposalCompany ?? '-'}</div>
                <div>프로젝트명: {selected.projectName ?? '-'}</div>
                <div>제안품목: {selected.itemSummary ?? '-'}</div>
                <div>제안가(VAT별도): {Number(selected.proposalPrice ?? 0).toLocaleString()} 원</div>
              </div>
              <h4 className="text-xs font-medium text-slate-600 mb-1">↓ 아래 값으로 덮어쓰기 (직접 수정 가능)</h4>
              <MappingEditor readonly={{ companyId: preview.companyId, empDisplay: preview.empDisplay }} form={form} setForm={setForm} />
              <div className="mt-3 text-xs text-slate-500">
                · 엔드유저를 비우면 기존 값이 유지됩니다<br />
                {selected.status === '일반' ? (
                  <>· status 가 <b>'유력'</b> 으로 승격됩니다 (현재: {selected.status})<br /></>
                ) : (
                  <>· status (<b>{selected.status}</b>) 는 그대로 유지 — 유력 이후 단계는 되돌리지 않음<br /></>
                )}
                · 날짜 / 비고 등 나머지 필드는 그대로 유지<br />
                · 견적서 PDF 가 영업건 첨부에 추가됩니다 (기존 견적서 첨부는 교체)
              </div>
            </div>
          )}

          {step === 'result' && result && (
            <div>
              <div className="text-center py-4">
                <div className="text-4xl mb-2">✅</div>
                <div className="text-base font-bold text-slate-800 mb-1">
                  {result.action === 'created' ? '신규 영업건 등록 완료' : '영업건 갱신 완료'}
                </div>
                <div className="text-sm text-slate-600">
                  매출일련번호: <span className="font-mono font-bold">{result.salesId}</span>
                </div>
              </div>
              <div className="text-xs text-slate-600 bg-slate-50 rounded p-3 space-y-1">
                <div>· 제안품목: {result.itemSummary || '-'}</div>
                <div>· 제안가(VAT별도): {result.proposalPrice.toLocaleString()} 원</div>
                <div>
                  · 견적서 PDF 자동 첨부:{' '}
                  {result.attached ? (
                    <span className="text-emerald-700 font-medium">성공</span>
                  ) : (
                    <span className="text-amber-700">실패 ({result.attachError ?? '알 수 없음'})</span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-between gap-2">
          <div>
            {(step === 'new-confirm' || step === 'update-search') && (
              <button
                type="button"
                onClick={() => setStep('choose')}
                className="btn btn-secondary"
              >
                ← 뒤로
              </button>
            )}
            {step === 'update-confirm' && (
              <button
                type="button"
                onClick={() => setStep('update-search')}
                className="btn btn-secondary"
              >
                ← 다른 영업건 선택
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {step === 'new-confirm' && (
              <button
                type="button"
                onClick={() => submitLink('new')}
                disabled={submitting}
                className="btn btn-primary"
              >
                {submitting ? '등록 중...' : '신규 등록 확인'}
              </button>
            )}
            {step === 'update-confirm' && (
              <button
                type="button"
                onClick={() => submitLink('update')}
                disabled={submitting}
                className="btn btn-warning-solid"
              >
                {submitting ? '갱신 중...' : '갱신 확인'}
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
            >
              {step === 'result' ? '완료' : '취소'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** 편집 가능한 매핑 표 — 사업자·영업담당자는 읽기전용, 나머지는 입력칸. */
function MappingEditor({
  readonly,
  form,
  setForm,
}: {
  readonly: { companyId: string; empDisplay: string };
  form: MappingForm;
  setForm: React.Dispatch<React.SetStateAction<MappingForm>>;
}) {
  const set = (k: keyof MappingForm) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));
  return (
    <table className="w-full text-xs border border-slate-200 rounded">
      <tbody>
        <ReadRow label="사업자" value={readonly.companyId} />
        <ReadRow label="영업담당자" value={readonly.empDisplay} />
        <EditRow label="제안업체 (수신)" value={form.proposalCompany} onChange={set('proposalCompany')} placeholder="제안업체" />
        <EditRow label="거래처담당자 (참조)" value={form.contactPerson} onChange={set('contactPerson')} placeholder="담당자" />
        <EditRow label="엔드유저" value={form.endUser} onChange={set('endUser')} placeholder="(선택) 비워둬도 등록됩니다" />
        <EditRow label="프로젝트명" value={form.projectName} onChange={set('projectName')} placeholder="프로젝트명" />
        <EditRow label="제안품목" value={form.itemSummary} onChange={set('itemSummary')} placeholder="제안품목" />
        <EditRow label="제안가 (VAT 별도)" value={form.proposalPrice} onChange={set('proposalPrice')} placeholder="0" inputMode="numeric" suffix="원" />
      </tbody>
    </table>
  );
}

function ReadRow({ label, value }: { label: string; value: string }) {
  return (
    <tr className="border-b border-slate-100 last:border-b-0">
      <td className="px-2 py-1.5 bg-slate-50 font-medium text-slate-600 w-32 whitespace-nowrap">
        {label}
      </td>
      <td className="px-2 py-1.5 text-slate-800">{value}</td>
    </tr>
  );
}

function EditRow({
  label,
  value,
  onChange,
  placeholder,
  inputMode,
  suffix,
}: {
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  inputMode?: 'numeric' | 'text';
  suffix?: string;
}) {
  return (
    <tr className="border-b border-slate-100 last:border-b-0">
      <td className="px-2 py-1.5 bg-slate-50 font-medium text-slate-600 w-32 whitespace-nowrap align-middle">
        {label}
      </td>
      <td className="px-2 py-1 text-slate-800">
        <div className="flex items-center gap-1">
          <input
            type="text"
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            inputMode={inputMode}
            className="ui-input ui-input-sm flex-1 min-w-0"
          />
          {suffix && <span className="text-slate-500 shrink-0">{suffix}</span>}
        </div>
      </td>
    </tr>
  );
}
